import {
  useReactTable,
  getCoreRowModel,
  flexRender,
  RowData
} from "@tanstack/react-table";
import RankingCell from "../RankingCell";
import { IonButton, IonIcon } from "@ionic/react";
import { warningSharp } from "ionicons/icons";
import { useCallback, useEffect, useRef, useState } from "react";

interface Props {
  data: any,
  handleSave:any,
  name?:string
}

function roundToThree(num:any) {
    if(!num){
      return num
    }
    return Math.round((num + Number.EPSILON) * 1000)/1000;
}

function useSkipper() {
  const shouldSkipRef = useRef(true)
  const shouldSkip = shouldSkipRef.current
  const skip = useCallback(() => {
    shouldSkipRef.current = false
  }, [])

  useEffect(() => {
    shouldSkipRef.current = true
  })

  return [shouldSkip, skip] as const
}

declare module '@tanstack/react-table' {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  interface TableMeta<TData extends RowData> {
    updateData: (rowIndex: number, columnId: string, value: unknown) => void
  }
}

const FloatCell =  ({ getValue, row, column: { id }, table }:any) => {
    const initialValue = roundToThree(getValue())
    const [value, setValue] = useState(initialValue)
    

    const onBlur = () => {
      table.options.meta?.updateData(row.index, id, value)
    }
    


    // useEffect only needed if table will be filtered,sorted of changed in anyway outside of the editor
    // useEffect(() => {
    //   setValue(initialValue)
    // // eslint-disable-next-line react-hooks/exhaustive-deps
    // }, [row.id])

    if('uuid' in row.original)
    {
      return <div className="center-all">
      <input
        style={{backgroundColor:"grey", maxWidth:'100%', textAlign:'center'}}
        value={value as string}
        onChange={(e) => setValue(e.target.value)}
        onBlur={onBlur}
        type="number"
      />
    </div>
    }
    
    return <div className="center-all">{value}</div>

    
  }

const TempCell = ({ getValue, row, column: { id }, table }:any) =>{
    const initialValue = getValue()
    const [value, setValue] = useState(initialValue)
    const [error, setError] = useState<any>(null);    

    const validate = (val:any) => {
      if (value === null || value === "") return null;
      if (!/^(\d{1,2}\sF)?$/.test(val)) return "Invalid Format: XX F";
      return null;
    };

    const onBlur = () => {
    const validationError = validate(value);
    if (!validationError) {
      table.options.meta?.updateData(row.index, id, value);
      setError(null);
    } else {
      setError(validationError);
    }
  };

    // useEffect only needed if table will be filtered,sorted of changed in anyway outside of the editor
    // useEffect(() => {
    //   setValue(initialValue)
    // }, [initialValue])

    if('uuid' in row.original)
    {
      return <div className="center-all">
        <div style={{maxWidth:'100%'}}>
      <input
      
        style={{backgroundColor:"grey", maxWidth:'100%',textAlign:'center'}}
        value={value as string}
        onChange={(e) => setValue(e.target.value)}
        onBlur={onBlur}
      />
      {error && <span style={{color:'red'}} >{error}</span>}
      </div>
    </div>
    }
    
    return <div className="center-all">{getValue() as string}</div>

    
  }

  const DegreeDaysRegressionTable: React.FC<Props> = ({
    data, handleSave, name
  }) => {

  const [tableData,setTableData] = useState([])
  const [autoResetPageIndex, skipAutoResetPageIndex] = useSkipper()

  useEffect(()=>{
    if(data){
      setTableData(data['regressions'])
    }
  },[data])
  


  const columns = [
    {
      id:'select',
      header: "",
      cell: (props: any) => <IonButton size="small" color="medium" onClick={()=>handleSave(props)}>Select</IonButton>,
      size: 100,
      enableColumnFilter: false,
      enableGlobalFilter: false,
    },
    {
      accessorKey: "regression",
      header: "Ranking",
      cell: RankingCell,
      size: 150,
      enableColumnFilter: false,
      enableGlobalFilter: false,
    },
    {
      accessorKey: "baseload_coefficient",
      header: "Baseload Coefficient",
      size: 175,
      cell:FloatCell,
      enableColumnFilter: false,
      enableGlobalFilter: false,
    },
    {
      accessorKey: "HDD_base_temp",
      header: "Base Temp (HDD)",
      cell: TempCell,
      size: 175,
      enableColumnFilter: false,
      enableGlobalFilter: false,
    },
    {
      accessorKey: "HDD_coef",
      header: "HDD Coefficient",
      cell: FloatCell,
      size: 200,
      enableColumnFilter: false,
      enableGlobalFilter: false,
    },
    {
      accessorKey: "CDD_base_temp",
      header: "Base Temp (CDD)",
      cell: TempCell,
      size: 200,
      enableColumnFilter: false,
      enableGlobalFilter: false,
    },
    {
      accessorKey: "CDD_coef",
      header: "CDD Coefficient",
      cell: FloatCell,
      size: 200,
      enableColumnFilter: false,
      enableGlobalFilter: false,
    },
    {
      accessorKey: "rSquared",
      header: "R - Squared",
      cell: FloatCell,
      size: 200,
      enableColumnFilter: false,
      enableGlobalFilter: false,
    },
    {
      accessorKey: "adjustedRSquared",
      header: "Adjusted R - Squared",
      cell: FloatCell,
      size: 200,
      enableColumnFilter: false,
      enableGlobalFilter: false,
    },
    {
      accessorKey: "crossValidatedRSquared",
      header: "Cross Validated R - Squared",
      cell: FloatCell,
      size: 200,
      enableColumnFilter: false,
      enableGlobalFilter: false,
    },
    {
      accessorKey: "standardError",
      header: "Standard Error",
      cell: FloatCell,
      size: 200,
      enableColumnFilter: false,
      enableGlobalFilter: false,
    },
    {
      accessorKey: "cvrmse",
      header: "Coefficient of Variation of the Root Mean Square Error",
      cell: FloatCell,
      size: 200,
      enableColumnFilter: false,
      enableGlobalFilter: false,
    }
  ];

  const table = useReactTable({
    data: data ? tableData : [],
    columns: columns,    
    getCoreRowModel: getCoreRowModel(),
    columnResizeMode: "onChange",
    autoResetPageIndex,
    meta: {
      updateData: (rowIndex, columnId, value) => {
        skipAutoResetPageIndex()
        setTableData((old:any) =>
          old.map((row:any, index:any) => {
            if (index === rowIndex) {
              return {
                ...old[rowIndex]!,
                [columnId]: value,
              }
            }
            return row
          }),
        )
      },
    },
  });

  if (!data || data['regressions'].length <= 0) {
    return null;
  }

  return (
    <div>
      <div>
          <div style={{display:'flex',justifyContent:'space-between'}}>
            <h3> Regression Analysis Results ({name})</h3>
          </div>
        </div>
        { data && data['regressions'].length > 0 ? <div style={{textAlign:'center',display:'flex',justifyContent:'center'}}>
        <IonIcon src={warningSharp} size="large"></IonIcon>
        <span style={{fontWeight:'bold', display:'flex', textAlign:'center', alignItems:'center', marginLeft:'0.3rem', marginRight:'0.3rem'}}>The Coefficient of Variation of the Root Mean Square Error should not be ABOVE 0.2 and be careful with Negative Coefficients.</span>
        <IonIcon src={warningSharp} size="large"></IonIcon>
      </div> : null}
      <div style={{textAlign:"center", padding:'0.5rem'}}>
        <div>Results are shown in order based on ranking.</div>
        <div>
          Press the select button next to the one you want to save for that meter.
        </div></div>
        <div style={{display:'flex',justifyContent:'center'}}>
      <div
        className="data-table"
        style={{ width: table.getTotalSize(), marginBottom: "2em", overflowX: "auto" }}
      >      
        {table.getHeaderGroups().map((headerGroup: any) => (
          <div key={headerGroup.id} className="tr">
            {headerGroup.headers.map((header: any) => {
              return (
                <div
                  key={header.id}
                  className="th"
                  style={{ width: header.getSize() }}

                >
                  {header.column.columnDef.header}
                  <div
                    onMouseDown={header.getResizeHandler()}
                    onTouchStart={header.getResizeHandler()}
                    className={`resizer ${
                      header.column.getIsResizing() ? "isResizing" : ""
                    }`}
                  ></div>
                </div>
              );
            })}
          </div>
        ))}
        {table.getRowModel().rows.map((row: any) => (
          <div
            key={row.id}
            className="tr"
            style={{ width: table.getTotalSize() }}
          >
            {row.getVisibleCells().map((cell: any) => {
              return (
                <div
                  key={cell.id}
                  className="td"
                  style={{ width: cell.column.getSize() }}
                >
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </div>
              );
            })}
          </div>
        ))}
      </div>
      </div>
    </div>
  );
};

export default DegreeDaysRegressionTable;
